// frontend/src/pages/Admin/AdminCalendarSettings.jsx
import React, { useState, useEffect, useMemo } from 'react';
import { getFacilityInfo, updateFacilityInfo } from '../../services/facilityService';
import { getCalendarBlocks, createCalendarBlock, deleteCalendarBlock } from '../../services/calendarBlockService';
import Button from '../../components/Button/Button';
import FormInput from '../../components/FormInput/FormInput';
import styles from './AdminCalendarSettings.module.css';

const formatCalendarDate = (value) => {
    if (!value) return 'N/A';
    const datePart = String(value).split('T')[0];
    const [year, month, day] = datePart.split('-').map(Number);
    if (!year || !month || !day) return 'N/A';
    return new Date(year, month - 1, day).toLocaleDateString();
};

const formatDateRange = (startValue, endValue) => {
    const start = formatCalendarDate(startValue);
    const end = formatCalendarDate(endValue);
    return start === end ? start : `${start} - ${end}`;
};

const AdminCalendarSettings = () => {
    const [facility, setFacility] = useState(null);
    const [blocks, setBlocks] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [expandedSeries, setExpandedSeries] = useState(new Set());
    const [expiryHoursInput, setExpiryHoursInput] = useState('48');

    // New block form state
    const [newBlock, setNewBlock] = useState({
        startDate: '',
        endDate: '',
        label: '',
        repeat: 'none',
        repeatEndDate: ''
    });

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [facilityData, blocksData] = await Promise.all([
                getFacilityInfo(),
                getCalendarBlocks()
            ]);
            setFacility(facilityData);
            setExpiryHoursInput(String(facilityData?.pendingExpiryHours ?? 48));
            setBlocks(blocksData);
        } catch (err) {
            setError('Failed to load settings.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleModeChange = async (mode) => {
        try {
            const updated = await updateFacilityInfo({ selectionMode: mode });
            setFacility(updated);
            setSuccess(`Selection mode updated to ${mode}.`);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Failed to update selection mode.');
        }
    };

    const handleCreateBlock = async (e) => {
        e.preventDefault();
        if (!newBlock.startDate || !newBlock.endDate) return;
        try {
            await createCalendarBlock(newBlock);
            const updatedBlocks = await getCalendarBlocks();
            setBlocks(updatedBlocks);
            setNewBlock({ startDate: '', endDate: '', label: '', repeat: 'none', repeatEndDate: '' });
            setSuccess('Calendar block(s) created.');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Failed to create block.');
        }
    };

    const handleDeleteBlock = async (block, deleteSeries = false) => {
        if (!window.confirm(deleteSeries ? 'Are you sure you want to delete the ENTIRE series?' : 'Are you sure you want to delete this block?')) return;

        try {
            await deleteCalendarBlock(block.id, deleteSeries);
            if (deleteSeries) {
                setBlocks(blocks.filter(b => b.batchId !== block.batchId));
                const newExpanded = new Set(expandedSeries);
                newExpanded.delete(block.batchId);
                setExpandedSeries(newExpanded);
            } else {
                setBlocks(blocks.filter(b => b.id !== block.id));
            }
            setSuccess('Block deleted.');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Failed to delete block.');
        }
    };

    const handleSaveExpiryHours = async () => {
        const hours = parseInt(expiryHoursInput, 10);
        if (isNaN(hours) || hours < 1) {
            setError('Please enter a valid number of hours (minimum 1).');
            return;
        }
        try {
            const updated = await updateFacilityInfo({ pendingExpiryHours: hours });
            setFacility(updated);
            setSuccess(`Reservation expiry updated to ${hours} hour${hours !== 1 ? 's' : ''}.`);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err) {
            setError('Failed to update reservation expiry.');
        }
    };

    const toggleSeries = (batchId) => {
        const newExpanded = new Set(expandedSeries);
        if (newExpanded.has(batchId)) {
            newExpanded.delete(batchId);
        } else {
            newExpanded.add(batchId);
        }
        setExpandedSeries(newExpanded);
    };

    // Group blocks by series
    const groupedBlocks = useMemo(() => {
        const groups = [];
        const seenBatches = new Set();

        blocks.forEach(block => {
            if (block.batchId) {
                if (!seenBatches.has(block.batchId)) {
                    seenBatches.add(block.batchId);
                    const seriesItems = blocks.filter(b => b.batchId === block.batchId);
                    groups.push({
                        type: 'series',
                        batchId: block.batchId,
                        label: block.label || 'Recurring Series',
                        count: seriesItems.length,
                        items: seriesItems,
                        // Show overall range for the series
                        startDate: seriesItems[0].startDate,
                        endDate: seriesItems[seriesItems.length - 1].endDate
                    });
                }
            } else {
                groups.push({
                    type: 'single',
                    ...block
                });
            }
        });
        return groups;
    }, [blocks]);

    if (isLoading) return <div className={styles.container}>Loading...</div>;

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <h2 className="page-main-title">Calendar Selection Settings</h2>
                <p>Configure how guests and users select dates on the reservation calendar.</p>
            </div>

            {error && <div className={styles.error}>{error}</div>}
            {success && <div className={styles.success}>{success}</div>}

            <section className={styles.section}>
                <h3>Selection Mode</h3>
                <div className={styles.modeSelector}>
                    <div
                        className={`${styles.modeOption} ${facility?.selectionMode === 'free' ? styles.active : ''}`}
                        onClick={() => handleModeChange('free')}
                    >
                        <h4>Free Selection</h4>
                        <p>Users can select any available dates individually.</p>
                    </div>
                    <div
                        className={`${styles.modeOption} ${facility?.selectionMode === 'blocks' ? styles.active : ''}`}
                        onClick={() => handleModeChange('blocks')}
                    >
                        <h4>Fixed Blocks</h4>
                        <p>Users must select from predefined blocks of dates.</p>
                    </div>
                </div>
            </section>

            <section className={styles.section}>
                <h3>Reservation Expiry</h3>
                <p>How many hours a pending reservation stays active before it is automatically expired if no payment is received.</p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginTop: '1rem' }}>
                    <input
                        type="number"
                        min="1"
                        max="720"
                        value={expiryHoursInput}
                        onChange={(e) => setExpiryHoursInput(e.target.value)}
                        style={{ width: '120px', padding: '0.5rem 0.75rem', borderRadius: '6px', border: '1px solid var(--border-color, #ccc)', fontSize: '1rem' }}
                    />
                    <span style={{ color: 'var(--text-secondary, #666)' }}>hours</span>
                    <Button variant="primary" onClick={handleSaveExpiryHours}>Save</Button>
                </div>
                <p style={{ fontSize: '0.85em', color: 'var(--text-secondary, #888)', marginTop: '0.5rem' }}>
                    Current setting: <strong>{facility?.pendingExpiryHours ?? 48} hour{(facility?.pendingExpiryHours ?? 48) !== 1 ? 's' : ''}</strong>
                </p>
            </section>

            <section className={styles.section}>
                <h3>Create New Block(s)</h3>
                <form className={styles.blockForm} onSubmit={handleCreateBlock}>
                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <FormInput
                                label="Start Date"
                                type="date"
                                value={newBlock.startDate}
                                onChange={(e) => setNewBlock({ ...newBlock, startDate: e.target.value })}
                                required
                            />
                        </div>
                        <div className={styles.formGroup}>
                            <FormInput
                                label="End Date"
                                type="date"
                                value={newBlock.endDate}
                                onChange={(e) => setNewBlock({ ...newBlock, endDate: e.target.value })}
                                required
                            />
                        </div>
                    </div>
                    <div className={styles.formGroupFull}>
                        <FormInput
                            label="Label (Optional)"
                            type="text"
                            placeholder="e.g. Winter Retreat"
                            value={newBlock.label}
                            onChange={(e) => setNewBlock({ ...newBlock, label: e.target.value })}
                        />
                    </div>

                    <div className={styles.formRow}>
                        <div className={styles.formGroup}>
                            <label className={styles.selectLabel}>Repeat</label>
                            <select
                                className={styles.selectInput}
                                value={newBlock.repeat}
                                onChange={(e) => setNewBlock({ ...newBlock, repeat: e.target.value })}
                            >
                                <option value="none">No Repeat</option>
                                <option value="weekly">Weekly</option>
                                <option value="monthly">Monthly</option>
                            </select>
                        </div>

                        {newBlock.repeat !== 'none' && (
                            <div className={styles.formGroup}>
                                <FormInput
                                    label="Repeat Until"
                                    type="date"
                                    value={newBlock.repeatEndDate}
                                    onChange={(e) => setNewBlock({ ...newBlock, repeatEndDate: e.target.value })}
                                    required
                                />
                            </div>
                        )}
                    </div>

                    <div className={styles.formActions}>
                        <Button type="submit" variant="primary">Add Block(s)</Button>
                    </div>
                </form>

                <div className={styles.listHeader}>
                    <h3>Existing Blocks</h3>
                </div>

                <div className={styles.tableContainer}>
                    <table className={styles.blockList}>
                        <thead>
                            <tr>
                                <th>Group / Date Range</th>
                                <th>Label</th>
                                <th>Type</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {groupedBlocks.map((group, idx) => (
                                <React.Fragment key={group.type === 'series' ? `batch-${group.batchId}` : `single-${group.id}-${idx}`}>
                                    <tr className={group.type === 'series' ? styles.seriesRow : ''}>
                                        <td>
                                            {group.type === 'series' && (
                                                <button
                                                    className={styles.expandBtn}
                                                    onClick={() => toggleSeries(group.batchId)}
                                                >
                                                    {expandedSeries.has(group.batchId) ? '▼' : '▶'}
                                                </button>
                                            )}
                                            {formatDateRange(group.startDate, group.endDate)}
                                            {group.type === 'series' && <span className={styles.itemCount}>({group.count})</span>}
                                        </td>
                                        <td>{group.label || '-'}</td>
                                        <td>
                                            <span className={`${styles.badge} ${group.type === 'series' ? styles.seriesBadge : styles.singleBadge}`}>
                                                {group.type === 'series' ? 'Series' : 'Single'}
                                            </span>
                                        </td>
                                        <td>
                                            <div className={styles.actionButtons}>
                                                <Button
                                                    variant={group.type === 'series' ? "danger" : "ghost"}
                                                    size="sm"
                                                    onClick={() => handleDeleteBlock(group.type === 'series' ? group.items[0] : group, group.type === 'series')}
                                                >
                                                    Delete {group.type === 'series' ? 'Series' : ''}
                                                </Button>
                                            </div>
                                        </td>
                                    </tr>
                                    {group.type === 'series' && expandedSeries.has(group.batchId) && group.items.map(item => (
                                        <tr key={`item-${item.id}`} className={styles.expandedRow}>
                                            <td style={{ paddingLeft: '2.5rem' }}>{formatDateRange(item.startDate, item.endDate)}</td>
                                            <td>{item.label || '-'}</td>
                                            <td><span className={styles.instanceLabel}>Instance</span></td>
                                            <td>
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    className={styles.deleteInstanceBtn}
                                                    onClick={() => handleDeleteBlock(item, false)}
                                                >
                                                    Delete
                                                </Button>
                                            </td>
                                        </tr>
                                    ))}
                                </React.Fragment>
                            ))}
                            {groupedBlocks.length === 0 && (
                                <tr>
                                    <td colSpan="4" style={{ textAlign: 'center', padding: '2rem' }}>No blocks defined.</td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </section>
        </div>
    );
};

export default AdminCalendarSettings;
